import {Popover, Progress} from "antd";
import dayjs from "dayjs";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {GiTwoCoins} from "react-icons/gi";
import {AiUsageServiceContext, AppContextContext} from "../../Contexts";
import {Feature} from "../../domain/Feature";
import {effectiveAiCreditLimit} from "../../domain/Organization";
import {Permission} from "../../domain/auth/Permission";
import {formatCreditsCompact} from "../../utils/FormatUtils";
import * as styles from "./AiCreditBadge.module.css";

const CREDIT_USAGE_FULL_PERCENT = 100;
const CREDIT_USAGE_WARNING_PERCENT = 80;

function AiCreditBadge() {
    const appContext = useContext(AppContextContext);
    const aiUsageService = useContext(AiUsageServiceContext);
    const [usedCredits, setUsedCredits] = useState<number>();

    const user = appContext.user;
    const organization = user?.organization;
    const limit = effectiveAiCreditLimit(organization);
    const tierName = organization?.aiTier?.name;
    const canShow = Boolean(
        user?.hasFeature(Feature.AI)
        && user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG)
        && organization?.id
        && limit !== undefined
    );

    useEffect(() => {
        let cancelled = false;

        setUsedCredits(undefined);

        if (!canShow || !organization?.id) {
            return;
        }

        aiUsageService.getSummary({
            dateFrom: dayjs().startOf("month").format("YYYY-MM-DD"),
            dateTo: dayjs().format("YYYY-MM-DD"),
            organizationId: organization.id
        }).then(summary => {
            if (!cancelled) {
                setUsedCredits(summary.price);
            }
        }).catch(() => {
            if (!cancelled) {
                setUsedCredits(undefined);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [aiUsageService, canShow, limit, organization?.id]);

    const usage = useMemo(() => {
        if (usedCredits === undefined || limit === undefined) {
            return undefined;
        }

        const percent = limit > 0 ? (usedCredits / limit) * 100 : 0;
        const remaining = Math.max(limit - usedCredits, 0);

        return {percent, remaining};
    }, [limit, usedCredits]);

    if (!canShow || usedCredits === undefined || limit === undefined || usage === undefined) {
        return null;
    }

    const badgeClassName = [
        styles.badge,
        usage.percent >= CREDIT_USAGE_FULL_PERCENT ? styles.badgeFull : undefined,
        usage.percent >= CREDIT_USAGE_WARNING_PERCENT && usage.percent < CREDIT_USAGE_FULL_PERCENT ? styles.badgeWarning : undefined,
    ].filter(Boolean).join(" ");

    const popoverContent = (
        <div className={styles.popover}>
            <div className={styles.popoverTitle}>AI credit usage</div>
            <div className={styles.detailGrid}>
                <span className={styles.label}>Tier</span>
                <span className={styles.value}>{tierName ?? "AI"}</span>
                <span className={styles.label}>Used this month</span>
                <span className={styles.value}>{formatCreditsCompact(usedCredits)}</span>
                <span className={styles.label}>Remaining</span>
                <span className={styles.value}>{formatCreditsCompact(usage.remaining)}</span>
                <span className={styles.label}>Limit</span>
                <span className={styles.value}>{formatCreditsCompact(limit)}</span>
            </div>
            <Progress
                className={styles.progress}
                percent={Math.min(usage.percent, CREDIT_USAGE_FULL_PERCENT)}
                showInfo={false}
                size="small"
                status={usage.percent >= CREDIT_USAGE_FULL_PERCENT ? "exception" : "normal"}
            />
            <Link className={styles.link} to="/ai-usage">Open AI usage &rarr;</Link>
        </div>
    );

    return (
        <Popover placement="bottomRight" content={popoverContent}>
            <Link
                to="/ai-usage"
                className={badgeClassName}
            >
                <GiTwoCoins aria-hidden={true} className={styles.icon}/>
                <span className={styles.tier}>{tierName ?? "AI"}</span>
                <span className={styles.divider}/>
                <span className={styles.usage}>{formatCreditsCompact(usedCredits)} used</span>
                <span className={styles.usage}>{formatCreditsCompact(usage.remaining)} left</span>
            </Link>
        </Popover>
    );
}

export default AiCreditBadge;
